import { Box, Radio } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import DataGrid from '../../../../../../table/DataGrid'
import DialogConfirmCancel from '../../../../../../dialog/DialogConfirm'
import FilterButtonUsersClass from './Filter/Button'
import CustomAvatar from '../../../../../../avatar/Avatar'
import useUsers from '../../../../../../../hooks/users/useUsers'
import useClasses from '../../../../../../../hooks/classes/useClasses'
import TextNotAssigned from '../../../../../../text/TextNotAssigned'
import { getObjectCurriculumElementsDR } from '../../../../../Assessment/utils'
import useAnalyticsCurriculum from '../../../../../../../hooks/analytics/useAnalyticsCurriculum'

const DialogStudentSelector = ({ onClose, open, dataId, onApply, type, view }) => {
  const curriculum = useSelector((state) => state?.analytics?.analyticsCurriculum?.datasets?.curriculum || {})
  const analytics = useSelector((state) => state?.analytics?.analyticsCurriculum?.datasets?.data?.data || {})
  const isLoadingList = useSelector((state) => state?.analytics?.analyticsCurriculum?.isLoadingList || false)
  const curriculumId = curriculum?._id || null
  const history = useSelector((state) => state?.analytics?.analyticsCurriculum?.history || null)

  const module = type === 'analyticsUsers' ? 'user' : 'class'
  const list = useSelector((state) => state?.analytics?.analyticsCurriculum?.list?.data || [])
  const totalList = useSelector((state) => state?.analytics?.analyticsCurriculum?.list?.count || 0)
  const years = useSelector((state) => state?.tenant?.years || [])
  const subjects = useSelector((state) => state?.subject?.list || [])
  const [dataSelected, setDataSelected] = useState(dataId)
  const [columnsTableState, setColumnsTableState] = useState([])

  const state = useSelector((state) => state?.filter?.[type] || {})
  const skip = useSelector((state) => state?.datatable?.[module]?.skip || 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.[module]?.rowsPerPage || 50)
  const { getClasses, getStudents } = useAnalyticsCurriculum()

  useEffect(() => {
    if (type === 'analyticsUsers') {
      const filter = {
        ...state?.filter,
        ids: analytics?.studentsIds || []
      }
      getStudents(view, history?._id, skip, rowsPerPage, filter)
    } else if (type === 'analyticsClasses') {
      const filter = {
        ...state?.filter,
        ids: analytics?.classIds || []
      }
      getClasses(view, curriculum?._id, history?._id, skip, rowsPerPage, filter)
    }
  }, [
    type,
    rowsPerPage,
    skip,
    state?.filter,
    curriculum?._id,
    history?._id,
    analytics?.studentsIds,
    analytics?.classIds
  ])

  const onConfirm = async (e) => {
    e.preventDefault()
    const find = list.find((row) => dataSelected === row._id)
    onApply(find)
    onClose()
  }

  const onSelected = (id) => {
    setDataSelected(id)
  }

  useEffect(() => {
    if (type === 'analyticsUsers') {
      const columnsTable = [
        {
          label: 'Photo',
          key: 'avatar',
          align: 'center',
          className: 'text-center',
          show: true,
          hide: false
        },
        { label: 'Code', key: 'code', show: true, hide: true },
        { label: 'Name', key: 'fullName', show: true, hide: true },
        {
          label: 'Year Level',
          key: 'yearLevelUser',
          show: true,
          hide: true
        }
      ]
      setColumnsTableState(columnsTable)
    } else if (type === 'analyticsClasses') {
      const columnsTable = [
        { label: 'Code', key: 'code', show: true, hide: true },
        {
          label: 'Year Level',
          key: 'yearLevel',
          show: true,
          hide: true
        }
      ]
      if (subjects?.length > 0) {
        columnsTable.push({
          label: 'Subject',
          key: 'subjectId',
          align: 'left',
          width: 250,
          show: true,
          hide: true,
          position: 6
        })
      }
      const elements = {}
      if (curriculum.elementsStructure) {
        elements[curriculum.elementsStructure?._id] = {
          label: curriculum.elementsStructure?.name,
          key: 'elementId',
          show: true,
          caret: true,
          hide: true,
          position: 4,
          width: 100,
          className: 'text-nowrap'
        }

        const subElement = curriculum.elementsStructure?.children[0]
        if (subElement) {
          elements[subElement?._id] = {
            label: subElement?.name,
            key: 'subElementId',
            show: true,
            caret: true,
            hide: true,
            position: 5,
            width: 100,
            className: 'text-nowrap'
          }
        }
      }

      Object.keys(elements).forEach((key) => {
        columnsTable.push(elements[key])
      })
      columnsTable.sort((a, b) => a.position - b.position)
      setColumnsTableState(columnsTable)
    }
  }, [type, subjects])

  return (
    <DialogConfirmCancel
      id={`${type}-selector`}
      open={open}
      disabledConfirm={isLoadingList}
      onCancel={onClose}
      onConfirm={onConfirm}
      title={`Select a ${type === 'analyticsUsers' ? 'student' : 'class'}`}
      onCloseHeader={true}
      maxWidth='lg'
    >
      <div className='h-screen-modal-content-footer overflow-auto'>
        <Box className='px-4 py-2 h-10 border-b border-b-2 border-primary text-primary-foreground flex items-center justify-between'>
          <FilterButtonUsersClass type={type} />
        </Box>
        <Box className='absolute top-20 bottom-4 inset-x-4'>
          <DataGrid
            id={`table-${type}-selector`}
            nameModule={module}
            rows={list.map((row, index) => {
              const data = row.data || {}
              const subject = subjects.find((item) => item._id === row.subjectId)
              const elements = getObjectCurriculumElementsDR(
                curriculum?.elements,
                curriculum?.elementsStructure,
                row?.curriculumElements || {}
              )
              const yearLevel = row.yearLevel?.map(
                (item) => years.find((year) => String(year._id) === String(item))?.name
              )
              return {
                ...row,
                index: index + 1 + rowsPerPage * skip,
                avatar: (
                  <div className='flex items-center justify-center'>
                    <CustomAvatar
                      size='small'
                      variant='circular'
                      alt={row?.code}
                      src={`photos/${row?.photo}`}
                    />
                  </div>
                ),
                ...data,
                yearLevelUser: row.yearLevel?.map((yearLevel) => yearLevel.name).join(', '),
                subjectId: subject?.name || <TextNotAssigned />,
                yearLevel: yearLevel?.join(', ') || '',
                elementId: elements?.element?.valueName || <TextNotAssigned />,
                subElementId: elements?.subElement?.valueName || <TextNotAssigned />,
                dataSelected: (
                  <Radio
                    id={`radio-id-user-${row._id}`}
                    checked={dataSelected === row._id}
                    onChange={() => onSelected(row._id)}
                    color='primary'
                  />
                )
              }
            })}
            loading={isLoadingList}
            totalRows={totalList}
            skip={skip}
            rowsPerPage={rowsPerPage}
            columns={[
              {
                label: '#',
                key: 'index',
                align: 'center',
                width: 10,
                show: true,
                hide: false
              },
              {
                show: true,
                key: 'dataSelected',
                align: 'center',
                width: 15,
                label: <></>
              },
              ...columnsTableState
            ]}
            labelNoRows={`You don't have any ${type === 'analyticsUsers' ? 'students' : 'classes'} yet`}
          />
        </Box>
      </div>
    </DialogConfirmCancel>
  )
}

export default DialogStudentSelector
